import { Col, FloatingLabel, Form, Spinner } from 'react-bootstrap';
import { FormProps } from './../../data/from-data/interface/form-props.interface';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
  GET_CUSTOMERS_REP_QUERY,
  SEARCH_CUSTOMERS
} from 'services/api/customer-rep';
import { JobClient, Role } from 'data/users';
import { debounce } from 'lodash';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../../styles/jobCards.css';
interface CreateStaffFormProps {
  handleInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  formProps: FormProps;
  error?: string;
  isEditing?: boolean;
  availableRoles?: Role[];
  rolesLoading?: boolean;
  isAdmin?: boolean;
}

const CreateEmployeeForm = ({
  handleInputChange,
  formProps,
  error,
  isEditing,
  availableRoles,
  rolesLoading,
  isAdmin
}: CreateStaffFormProps) => {
  const [searchResults, setSearchResults] = useState<JobClient[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  useEffect(() => {
    if (
      isEditing &&
      formProps?.name === 'client' &&
      formProps.value &&
      typeof formProps.value === 'object'
    ) {
      if ('organizationName' in formProps.value) {
        setSearchTerm(formProps.value.organizationName);
      }
    }
  }, [isEditing, formProps?.value]);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsSearching(true);
      setHasSearched(true);
      try {
        const response = await SEARCH_CUSTOMERS(query);
        const sorted = [...(response.data ?? [])].sort((a, b) =>
          (a.organizationName ?? '').localeCompare(
            b.organizationName ?? '',
            undefined,
            { sensitivity: 'base' }
          )
        );
        setSearchResults(sorted);
      } catch (error) {
        console.error('Error searching customers:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    []
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setHasSearched(false);
    if (value.length >= 2) {
      debouncedSearch(value);
    } else {
      setSearchResults([]);
    }
  };

  const handleRolesChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      option => option.value
    );
    handleInputChange({
      target: {
        name: formProps?.name || '',
        value: selectedOptions
      }
    } as any);
  };

  if (['isAdmin', 'isFieldOperator'].includes(formProps?.name || '')) {
    return null;
  }

  return (
    <>
      {formProps?.type === 'select' && formProps?.name === 'roles' ? (
        !isAdmin ? (
          <Col sm={12} md={12}>
            {rolesLoading && !availableRoles ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Form.Group className="">
                <Form.Label>{formProps?.label}</Form.Label>
                <Form.Select
                  name="roles"
                  multiple={formProps?.multiple}
                  value={formProps?.value || []}
                  onChange={handleRolesChange}
                  isInvalid={!!error}
                  aria-label={formProps?.label}
                >
                  <option value="" disabled>
                    Select Roles
                  </option>
                  {availableRoles?.map(role => (
                    <option
                      key={(role as any).id || role._id}
                      value={(role as any).id || role._id}
                    >
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {error}
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Col>
        ) : null
      ) : formProps?.type === 'select' && formProps?.name === 'client' ? (
        <Col sm={12} md={12}>
          <Form.Group className="position-relative">
            <Form.Label>{formProps?.label}</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search company..."
              value={searchTerm}
              onChange={handleSearch}
              isInvalid={!!error}
              aria-label={formProps?.label}
              className="company-dropdown"
            />
            {isSearching && (
              <Spinner
                animation="border"
                size="sm"
                className="position-absolute"
                style={{ right: '10px', top: '30px' }}
              />
            )}
            {searchTerm.length >= 2 &&
              !isSearching &&
              searchResults.length === 0 &&
              hasSearched && (
                <div
                  className="mt-1 bg-white border rounded shadow-sm company-dropdown-results p-2 text-center text-muted"
                  style={{ zIndex: 1000 }}
                >
                  No result found
                </div>
              )}
            {searchResults?.length > 0 && (
              <div
                className="mt-1 bg-white border rounded shadow-sm company-dropdown-results"
                style={{ zIndex: 1000 }}
              >
                {searchResults?.map(company => (
                  <div
                    key={(company as any).id || company._id}
                    className="p-2 cursor-pointer hover:bg-gray-100 max-w-25"
                    onClick={() => {
                      handleInputChange({
                        target: {
                          name: formProps?.name || '',
                          value: (company as any).id || company._id
                        }
                      } as any);
                      setSearchTerm(company.organizationName);
                      setSearchResults([]);
                      setHasSearched(false);
                    }}
                  >
                    {company.organizationName}
                  </div>
                ))}
              </div>
            )}
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      ) : formProps?.type === 'tel' ? (
        <Col sm={12} md={12}>
          <Form.Group className="">
            <Form.Label>{formProps?.label}</Form.Label>
            <PhoneInput
              country={'us'}
              value={formProps?.value || ''}
              onChange={value => {
                // Pass the raw value as-is, do not prepend '+' or country code
                const syntheticEvent = {
                  target: {
                    name: formProps?.name || '',
                    value: value
                  }
                };
                handleInputChange(syntheticEvent as any);
              }}
              inputClass={`form-control ${error ? 'is-invalid' : ''}`}
              inputStyle={{
                width: '100%',
                height: '38px',
                borderColor: error ? '#dc3545' : undefined
              }}
              specialLabel={''}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </Form.Group>
        </Col>
      ) : (
        <Col sm={12} md={12}>
          <Form.Group className="">
            <Form.Label>{formProps?.label}</Form.Label>
            {formProps?.required !== undefined && formProps.required ? (
              <Form.Control
                type={formProps?.type}
                placeholder={formProps?.placeholder}
                name={formProps?.name}
                value={formProps?.value || ''}
                onChange={handleInputChange}
                required
                isInvalid={!!error}
              />
            ) : (
              <Form.Control
                type={formProps?.type}
                placeholder={formProps?.placeholder}
                name={formProps?.name}
                value={formProps?.value || ''}
                onChange={handleInputChange}
                isInvalid={!!error}
              />
            )}
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      )}
    </>
  );
};

export default CreateEmployeeForm;

export const CreateCustomerForm = ({
  formProps,
  handleInputChange,
  error
}: {
  formProps?: FormProps;
  handleInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  error?: string;
}) => {
  const { data, isLoading } = useQuery(
    'getAllCustomersReps',
    GET_CUSTOMERS_REP_QUERY,
    {
      staleTime: 3 * 60 * 60 * 1000
    }
  );

  return (
    <>
      {formProps?.type === 'select' ? (
        <Col sm={12} md={12}>
          {isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <FloatingLabel
              controlId={`${formProps?.label}-name`}
              label={formProps?.label}
            >
              <Form.Select
                name="contactPerson"
                value={formProps.value?.id || ''}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Representative</option>
                {data?.data?.map(customerRep => (
                  <option
                    value={(customerRep as any).id || customerRep._id}
                    key={(customerRep as any).id || customerRep._id}
                  >
                    {customerRep.firstName} {customerRep.lastName}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          )}
        </Col>
      ) : formProps?.type === 'tel' ? (
        <Col sm={12} md={12}>
          <Form.Group className="">
            <Form.Label>{formProps?.label}</Form.Label>
            <PhoneInput
              country={'us'}
              value={formProps?.value || ''}
              onChange={value => {
                // Pass the raw value as-is, do not prepend '+' or country code
                const syntheticEvent = {
                  target: {
                    name: formProps?.name || '',
                    value: value
                  }
                };
                handleInputChange(syntheticEvent as any);
              }}
              inputClass={`form-control ${error ? 'is-invalid' : ''}`}
              inputStyle={{
                width: '100%',
                height: '38px',
                borderColor: error ? '#dc3545' : undefined
              }}
              specialLabel={''}
            />
          </Form.Group>
        </Col>
      ) : (
        <Col sm={12} md={12}>
          <Form.Group className="">
            <Form.Label>{formProps?.label}</Form.Label>
            {formProps?.required !== undefined && formProps.required ? (
              <Form.Control
                type={formProps?.type}
                placeholder={formProps?.placeholder}
                name={formProps?.name}
                value={formProps?.value || ''}
                onChange={handleInputChange}
                required
              />
            ) : (
              <Form.Control
                type={formProps?.type}
                placeholder={formProps?.placeholder}
                name={formProps?.name}
                value={formProps?.value || ''}
                onChange={handleInputChange}
              />
            )}
          </Form.Group>
        </Col>
      )}
    </>
  );
};
